/**
 * Color Helper Functions - Shared Brand Color Computation
 * Used by both web and portal apps to ensure consistent color calculations
 */

/**
 * Normalize and validate hex color values
 */
export function normalizeHexColor(value, fallback) {
  const color = typeof value === 'string' ? value.trim() : '';
  if (/^#[0-9a-fA-F]{6}$/.test(color)) {
    return color.toUpperCase();
  }
  return fallback;
}

/**
 * Convert hex color to RGB
 */
export function hexToRgb(hex) {
  const value = normalizeHexColor(hex, '#1D4ED8');
  return {
    r: parseInt(value.slice(1, 3), 16),
    g: parseInt(value.slice(3, 5), 16),
    b: parseInt(value.slice(5, 7), 16),
  };
}

/**
 * Convert RGB to hex color
 */
export function rgbToHex({ r, g, b }) {
  const toHex = (channel) => Math.max(0, Math.min(255, channel)).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

/**
 * Convert RGB to HSL
 */
export function rgbToHsl({ r, g, b }) {
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;

  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  const delta = max - min;

  let h = 0;
  const l = (max + min) / 2;
  let s = 0;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));

    switch (max) {
      case rNorm:
        h = 60 * (((gNorm - bNorm) / delta) % 6);
        break;
      case gNorm:
        h = 60 * ((bNorm - rNorm) / delta + 2);
        break;
      default:
        h = 60 * ((rNorm - gNorm) / delta + 4);
        break;
    }
  }

  if (h < 0) h += 360;

  return {
    h: Math.round(h),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Convert HSL to RGB
 */
export function hslToRgb(h, s, l) {
  const hue = ((h % 360) + 360) % 360;
  const sat = Math.min(Math.max(s, 0), 100) / 100;
  const light = Math.min(Math.max(l, 0), 100) / 100;

  const c = (1 - Math.abs(2 * light - 1)) * sat;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = light - c / 2;

  let rPrime = 0;
  let gPrime = 0;
  let bPrime = 0;

  if (hue < 60) {
    rPrime = c;
    gPrime = x;
  } else if (hue < 120) {
    rPrime = x;
    gPrime = c;
  } else if (hue < 180) {
    gPrime = c;
    bPrime = x;
  } else if (hue < 240) {
    gPrime = x;
    bPrime = c;
  } else if (hue < 300) {
    rPrime = x;
    bPrime = c;
  } else {
    rPrime = c;
    bPrime = x;
  }

  return {
    r: Math.round((rPrime + m) * 255),
    g: Math.round((gPrime + m) * 255),
    b: Math.round((bPrime + m) * 255),
  };
}

/**
 * Mix two hex colors with a ratio (0.0 to 1.0)
 */
export function mixHex(colorA, colorB, ratio = 0.5) {
  const a = hexToRgb(colorA);
  const b = hexToRgb(colorB);
  const t = Math.min(Math.max(ratio, 0), 1);

  return rgbToHex({
    r: Math.round(a.r * (1 - t) + b.r * t),
    g: Math.round(a.g * (1 - t) + b.g * t),
    b: Math.round(a.b * (1 - t) + b.b * t),
  });
}

/**
 * Darken a hex color by mixing with black
 */
export function darkenHex(color, amount = 0.12) {
  return mixHex(color, '#000000', amount);
}

/**
 * Desaturate a hex color
 */
export function desaturateHex(color, amount = 0.5) {
  const { r, g, b } = hexToRgb(color);
  const gray = Math.round(r * 0.299 + g * 0.587 + b * 0.114);
  const t = Math.min(Math.max(amount, 0), 1);

  return rgbToHex({
    r: Math.round(r * (1 - t) + gray * t),
    g: Math.round(g * (1 - t) + gray * t),
    b: Math.round(b * (1 - t) + gray * t),
  });
}

/**
 * Get the most readable foreground color (white or dark) for a given background
 * Determines if black or white text is more readable based on luminance
 */
export function getReadableTextColor(color) {
  const hex = normalizeHexColor(color, '#FFFFFF');
  const red = parseInt(hex.slice(1, 3), 16);
  const green = parseInt(hex.slice(3, 5), 16);
  const blue = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * red) + (0.587 * green) + (0.114 * blue);
  return luminance > 165 ? '#0F172A' : '#FFFFFF';
}

/**
 * Get the most readable foreground color for primary color
 * Uses a different luminance threshold than getReadableTextColor
 */
export function getPrimaryForegroundColor(primaryHex) {
  const { r, g, b } = hexToRgb(primaryHex);
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance > 0.74 ? '#0F172A' : '#FFFFFF';
}

/**
 * Normalize badge text color - only allows white or dark
 */
export function normalizeBadgeTextColor(value, fallback) {
  const normalized = normalizeHexColor(value, fallback).toUpperCase();
  return normalized === '#FFFFFF' ? '#FFFFFF' : '#0F172A';
}

/**
 * Get hero overlay color - desaturated and lightened primary
 */
export function getHeroOverlayColor(primaryHex) {
  return mixHex(desaturateHex(primaryHex, 0.45), '#FFFFFF', 0.98);
}

/**
 * Compute secondary color from primary using HSL adjustments
 */
export function getSecondaryColor(primaryHex) {
  const { h, s, l } = rgbToHsl(hexToRgb(primaryHex));
  const hueShift = 20 + ((35 - 20) * Math.min(Math.max(h / 360, 0), 1));
  const saturationReduction = 25 + ((45 - 25) * Math.min(Math.max(l / 100, 0), 1));
  const lightnessIncrease = 5 + ((15 - 5) * Math.min(Math.max(s / 100, 0), 1));

  const secondaryH = Math.round((h + hueShift) % 360);
  const secondaryS = Math.round(Math.min(Math.max(s * (1 - saturationReduction / 100), 0), 100));
  const secondaryL = Math.round(Math.min(Math.max(l + lightnessIncrease, 0), 100));

  return {
    secondaryHex: rgbToHex(hslToRgb(secondaryH, secondaryS, secondaryL)),
  };
}

/**
 * Get spotlight accent color from primary
 */
const PRIMARY_HUE_FAMILIES = [
  { family: 'red', min: 345, max: 360 },
  { family: 'red', min: 0, max: 15 },
  { family: 'orange', min: 16, max: 45 },
  { family: 'green', min: 75, max: 155 },
  { family: 'teal', min: 156, max: 190 },
  { family: 'blue', min: 191, max: 255 },
  { family: 'purple', min: 256, max: 320 },
];

const SPOTLIGHT_FAMILIES = {
  gold: {
    hueMin: 38,
    hueMax: 48,
    saturationMin: 42,
    saturationMax: 58,
    lightnessMin: 52,
    lightnessMax: 68,
    defaultHsl: [42, 49, 60],
  },
  champagne: {
    hueMin: 38,
    hueMax: 50,
    saturationMin: 30,
    saturationMax: 48,
    lightnessMin: 64,
    lightnessMax: 82,
    defaultHsl: [43, 39, 72],
  },
  roseGold: {
    hueMin: 8,
    hueMax: 24,
    saturationMin: 35,
    saturationMax: 55,
    lightnessMin: 58,
    lightnessMax: 74,
    defaultHsl: [16, 45, 66],
  },
  amber: {
    hueMin: 32,
    hueMax: 44,
    saturationMin: 55,
    saturationMax: 75,
    lightnessMin: 48,
    lightnessMax: 64,
    defaultHsl: [38, 65, 56],
  },
  deepBronze: {
    hueMin: 24,
    hueMax: 36,
    saturationMin: 38,
    saturationMax: 58,
    lightnessMin: 34,
    lightnessMax: 50,
    defaultHsl: [30, 48, 42],
  },
  warmSand: {
    hueMin: 34,
    hueMax: 48,
    saturationMin: 24,
    saturationMax: 42,
    lightnessMin: 68,
    lightnessMax: 84,
    defaultHsl: [40, 32, 76],
  },
};

const PRIMARY_TO_SPOTLIGHT = {
  green: 'gold',
  blue: 'champagne',
  purple: 'roseGold',
  red: 'amber',
  orange: 'deepBronze',
  teal: 'warmSand',
  neutral: 'gold',
};

function clampNumber(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function lerpNumber(min, max, t) {
  return min + (max - min) * t;
}

function classifyPrimaryHueFamily(hsl) {
  if (hsl.s < 12) return 'neutral';
  const match = PRIMARY_HUE_FAMILIES.find((range) => hsl.h >= range.min && hsl.h <= range.max);
  return match?.family || 'neutral';
}

export function getSpotlightAccentFromPrimary(primaryHex) {
  const primaryHsl = rgbToHsl(hexToRgb(primaryHex));
  const primaryFamily = classifyPrimaryHueFamily(primaryHsl);
  const spotlightFamily = PRIMARY_TO_SPOTLIGHT[primaryFamily] || 'gold';
  const family = SPOTLIGHT_FAMILIES[spotlightFamily] || SPOTLIGHT_FAMILIES.gold;
  const [defaultH, defaultS, defaultL] = family.defaultHsl;
  const hueSeed = clampNumber(primaryHsl.h / 360, 0, 1);
  const satSeed = clampNumber(primaryHsl.s / 100, 0, 1);
  const lightSeed = clampNumber(primaryHsl.l / 100, 0, 1);
  const dynamicHue = lerpNumber(family.hueMin, family.hueMax, hueSeed);
  const dynamicSat = lerpNumber(family.saturationMin, family.saturationMax, satSeed);
  const dynamicLight = lerpNumber(family.lightnessMin, family.lightnessMax, lightSeed);
  const h = clampNumber(Math.round(lerpNumber(defaultH, dynamicHue, 0.3)), family.hueMin, family.hueMax);
  const s = clampNumber(Math.round(lerpNumber(defaultS, dynamicSat, 0.35)), family.saturationMin, family.saturationMax);
  const l = clampNumber(Math.round(lerpNumber(defaultL, dynamicLight, 0.35)), family.lightnessMin, family.lightnessMax);

  return rgbToHex(hslToRgb(h, s, l));
}

/**
 * Compute all brand color values from primary color and settings
 * Returns an object with all computed token colors
 */
export function computeBrandColors(primaryColor, settings = {}) {
  const primary = normalizeHexColor(primaryColor, '#1D4ED8');
  const secondary = getSecondaryColor(primary).secondaryHex;
  const spotlight = settings.spotlightColor || '#FFC107';
  const primaryHover = darkenHex(primary, 0.12);
  const primarySoft = mixHex(primary, '#FFFFFF', 0.9);
  const primaryForeground = getPrimaryForegroundColor(primary);
  const secondaryHover = darkenHex(secondary, 0.12);
  const secondarySoft = mixHex(secondary, '#FFFFFF', 0.9);
  const secondaryForeground = getPrimaryForegroundColor(secondary);
  const spotlightHover = darkenHex(spotlight, 0.12);
  const spotlightSoft = mixHex(spotlight, '#FFFFFF', 0.9);
  const spotlightForeground = getReadableTextColor(spotlight);

  return {
    primary,
    primaryForeground,
    primaryHover,
    primarySoft,
    secondary,
    secondaryForeground,
    secondaryHover,
    secondarySoft,
    spotlight,
    spotlightForeground,
    spotlightHover,
    spotlightSoft,
  };
}

/**
 * Create the canonical generated brand theme from persisted source colors.
 *
 * Persisted inputs are intentionally limited to primary, secondary, and accent.
 * All hover, soft, foreground, surface, status, badge, and text tokens are
 * generated at runtime from those source values.
 */
export function createBrandTheme({ primaryColor, secondaryColor, accentColor } = {}) {
  const primary = normalizeHexColor(primaryColor, '#1D4ED8');
  const secondary = normalizeHexColor(secondaryColor, getSecondaryColor(primary).secondaryHex);
  const spotlight = normalizeHexColor(accentColor, getSpotlightAccentFromPrimary(primary));
  const background = mixHex(desaturateHex(primary, 0.7), '#FFFFFF', 0.86);
  const surface = mixHex(desaturateHex(primary, 0.45), '#FFFFFF', 0.94);
  const card = mixHex(desaturateHex(primary, 0.45), '#FFFFFF', 0.98);

  return {
    primary,
    primaryHover: darkenHex(primary, 0.12),
    primarySoft: mixHex(primary, '#FFFFFF', 0.9),
    primaryForeground: getPrimaryForegroundColor(primary),
    secondary,
    secondaryHover: darkenHex(secondary, 0.12),
    secondarySoft: mixHex(secondary, '#FFFFFF', 0.9),
    secondaryForeground: getPrimaryForegroundColor(secondary),
    spotlight,
    spotlightHover: darkenHex(spotlight, 0.12),
    spotlightSoft: mixHex(spotlight, '#FFFFFF', 0.9),
    spotlightForeground: getPrimaryForegroundColor(spotlight),
    background,
    surface,
    card,
    border: mixHex(desaturateHex(primary, 0.72), '#FFFFFF', 0.72),
    text: '#0F172A',
    muted: '#6B7280',
    success: '#10B981',
    successForeground: '#FFFFFF',
    warning: '#F59E0B',
    warningForeground: '#111827',
    danger: '#EF4444',
    dangerForeground: '#FFFFFF',
  };
}
